import { useCallback, useEffect, useMemo, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { fetchHome, freshResponse, niceBudget, rememberOrigin, rememberedOrigin } from "@/lib/recommendations";

/**
 * The recommendation sections for this visitor: origin (remembered once
 * picked, otherwise the server's guess from the country), a round budget in
 * their currency, and the answer. `data` is null while loading; `failed`
 * says the optional service could not be reached. No fabricated fallback.
 * `remember: false` (the home page) always uses the server's detection.
 */
export default function useRecommendations({ remember = true } = {}) {
  const { currency, currencies } = useSettings();
  const [origin, setOriginState] = useState(() => (remember ? rememberedOrigin() : ""));
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const budget = useMemo(() => niceBudget(currency, currencies), [currency, currencies]);

  useEffect(() => {
    let live = true;
    setData(null); setFailed(false);
    const refresh = () => fetchHome({ origin, budget: budget.xof })
      .then((answer) => { if (live) { setData(freshResponse(answer)); setFailed(false); } })
      .catch(() => { if (live) { setFailed(true); setData(null); } });
    refresh();
    const visible = () => { if (document.visibilityState === "visible") refresh(); };
    const interval = setInterval(visible, 120_000);
    document.addEventListener("visibilitychange", visible);
    return () => { live = false; clearInterval(interval); document.removeEventListener("visibilitychange", visible); };
  }, [origin, budget.xof]);

  useEffect(() => {
    const expiries = (data?.sections || []).flatMap((s) => (s.items || []).map((card) => Date.parse(card.expires_at))).filter(Number.isFinite);
    if (!expiries.length) return undefined;
    const timer = setTimeout(() => setData((current) => freshResponse(current)),
      Math.min(2_147_483_647, Math.max(1, Math.min(...expiries) - Date.now() + 1)));
    return () => clearTimeout(timer);
  }, [data]);

  const setOrigin = useCallback((code) => { rememberOrigin(code); setOriginState(code); }, []);
  const section = useCallback((id) => data?.sections?.find((s) => s.id === id) || { id, items: [] }, [data]);
  const empty = !!data && (data.sections || []).every((s) => !s.items?.length);
  return { data, failed, empty, loading: !data && !failed, origin: data?.origin, setOrigin, section, budget };
}
